import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    useTheme,
} from '@material-ui/core'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../helpers'
import { RFPBidThread, ThreadMessage } from './RFPBidThread'

interface MessageLinkPayload {
    apartment_name: string
    vendor_name: string
    rfp_name: string
    rfp_id: number
    property_notes: string
    vendor_notes: string
    login_url: string
    messages: {
        id: number
        content: string
        created_date: string
        sender_name: string
        is_vendor: boolean
        attachments: { id: number; file: string | null; uploaded: boolean }[]
    }[]
}

const VendorLoginCta = ({ href }: { href: string }) => (
    <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        style={{ gap: 8 }}
    >
        <Typography
            color="textSecondary"
            style={{ fontSize: 13, textAlign: 'center' }}
        >
            See other proposals and manage your work orders.
        </Typography>
        <Button
            variant="contained"
            color="primary"
            href={href}
            style={{ textTransform: 'none', fontWeight: 700 }}
        >
            Login to EZOS
        </Button>
    </Box>
)

const toThread = (data: MessageLinkPayload): ThreadMessage[] =>
    data.messages.map((message) => ({
        id: message.id,
        content: message.content,
        created_date: message.created_date,
        sender_name: message.sender_name,
        isMine: message.is_vendor,
        attachments: message.attachments,
    }))

export const RfpMessagePage = () => {
    const theme = useTheme()
    const { link_token } = useParams<{ link_token: string }>()
    const [loading, setLoading] = useState(true)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [payload, setPayload] = useState<MessageLinkPayload | null>(null)

    useEffect(() => {
        setLoading(true)
        axiosInstance
            .get<MessageLinkPayload>('service-contract/rfp-message-link/', {
                params: { link_token },
            })
            .then((res) => {
                setPayload(res.data)
                setError(null)
            })
            .catch((err) => {
                setError(
                    err?.response?.data?.error ||
                        'Unable to open this conversation.',
                )
            })
            .finally(() => setLoading(false))
    }, [link_token])

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" style={{ padding: 80 }}>
                <CircularProgress />
            </Box>
        )
    }

    if (error || !payload) {
        return (
            <Box padding={4} maxWidth={640} margin="0 auto">
                <Typography style={{ fontWeight: 700, fontSize: 20 }}>
                    Conversation unavailable
                </Typography>
                <Typography color="textSecondary" style={{ marginTop: 8 }}>
                    {error ||
                        'This conversation link is invalid or has expired.'}
                </Typography>
                <Box style={{ marginTop: 16 }}>
                    <VendorLoginCta href="/login" />
                </Box>
            </Box>
        )
    }

    return (
        <Box padding={3} maxWidth={720} margin="0 auto">
            <Box
                style={{
                    border: '1px solid #E6E8E8',
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: theme.palette.common.white,
                    minHeight: '72vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <RFPBidThread
                    title={payload.rfp_name}
                    subtitle={`${payload.apartment_name} · ${payload.vendor_name}`}
                    propertyNotes={payload.property_notes}
                    vendorNotes={payload.vendor_notes}
                    messages={toThread(payload)}
                    sending={sending}
                    onSend={async (text, files) => {
                        const body = new FormData()
                        body.append('link_token', link_token)
                        if (text) {
                            body.append('content', text)
                        }
                        if (files.length > 0) {
                            body.append('attachments_len', String(files.length))
                            files.forEach((file, index) => {
                                body.append(`attachment${index}`, file)
                            })
                        }
                        setSending(true)
                        try {
                            const res = await axiosInstance.post<MessageLinkPayload>(
                                'service-contract/rfp-message-link/',
                                body,
                            )
                            setPayload(res.data)
                        } catch (err: any) {
                            toast.error(
                                err?.response?.data?.error ||
                                    'Unable to send that message.',
                            )
                            throw err
                        } finally {
                            setSending(false)
                        }
                    }}
                    footerNote={<VendorLoginCta href={payload.login_url} />}
                />
            </Box>
        </Box>
    )
}
